class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.isOpen = false;

    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');
    form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    if (searchTerm.length < 2) {
      this.close();
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (searchTerm.length < 2) return;

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    })
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll('li');
    let activeElement = this.querySelector('li');

    if (moveUp && !selectedElement) return;

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');

    if (selectedProduct) selectedProduct.click();
  }

  suggestUrl(searchTerm) {
    const form = this.querySelector('form.search');
    const url = new URL((form && form.action) || '/search', window.location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'last');
    url.searchParams.set('view', 'suggest');
    return url.toString();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    this.searchTicket = (this.searchTicket || 0) + 1;
    const ticket = this.searchTicket;

    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }

    if (this.cachedResults[queryKey]) {
      this.renderProductResults(this.cachedResults[queryKey], searchTerm);
      return;
    }

    this.abortController = new AbortController();
    fetch(this.suggestUrl(searchTerm), { signal: this.abortController.signal })
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then((text) => {
        if (ticket !== this.searchTicket) return;
        const data = this.parseSuggestPayload(text);
        if (!data) {
          this.renderSearchFallback(searchTerm);
          return;
        }

        this.cachedResults[queryKey] = data;
        this.renderProductResults(data, searchTerm);
      })
      .catch((err) => {
        if (err && err.name === 'AbortError') return;
        if (ticket !== this.searchTicket) return;
        this.renderSearchFallback(searchTerm);
      });
  }

  parseSuggestPayload(text) {
    if (!text || text.trim().charAt(0) === '<') return null;
    try {
      const data = JSON.parse(text);
      if (!data || !Array.isArray(data.products)) return null;
      return data;
    } catch (error) {
      return null;
    }
  }

  renderProductResults(data, searchTerm) {
    const products = (data && data.products) || [];
    const collection = data && data.collection && data.collection.url && data.collection.title
      ? data.collection
      : null;
    if (!products.length && !collection) {
      this.renderSearchFallback(searchTerm);
      return;
    }

    const results = document.createElement('div');
    results.id = 'predictive-search-results';

    const list = document.createElement('ul');
    list.id = 'predictive-search-results-list';
    list.className = 'predictive-search__results-list list-unstyled';
    list.setAttribute('role', 'listbox');

    if (collection) {
      const item = document.createElement('li');
      item.id = 'predictive-search-option-collection';
      item.className = 'predictive-search__list-item';
      item.setAttribute('role', 'option');
      item.setAttribute('aria-selected', 'false');

      const link = document.createElement('a');
      link.href = collection.url;
      link.className = 'predictive-search__item predictive-search__item--collection link link--text';
      link.tabIndex = -1;
      link.textContent = collection.title;
      item.appendChild(link);
      list.appendChild(item);
    }

    products.forEach((product, index) => {
      if (!product || !product.url || !product.title) return;

      const item = document.createElement('li');
      item.id = `predictive-search-option-${index + 1}`;
      item.className = 'predictive-search__list-item';
      item.setAttribute('role', 'option');
      item.setAttribute('aria-selected', 'false');

      const link = document.createElement('a');
      link.href = product.url;
      link.className = 'predictive-search__item predictive-search__item--link link link--text';
      link.tabIndex = -1;

      if (product.image) {
        const image = document.createElement('img');
        image.className = 'predictive-search__image';
        image.src = product.image;
        image.alt = '';
        image.width = 48;
        image.height = 48;
        image.loading = 'lazy';
        image.decoding = 'async';
        link.appendChild(image);
      }

      const content = document.createElement('div');
      content.className = 'predictive-search__item-content';

      const title = document.createElement('h3');
      title.className = 'predictive-search__item-heading h5';
      title.textContent = product.title;
      content.appendChild(title);

      if (product.price) {
        const price = document.createElement('div');
        price.className = 'predictive-search__price';
        price.textContent = product.price;
        content.appendChild(price);
      }

      link.appendChild(content);
      item.appendChild(link);
      list.appendChild(item);
    });

    const termOption = document.createElement('li');
    termOption.id = 'predictive-search-option-search-keywords';
    termOption.className = 'predictive-search__list-item';
    termOption.setAttribute('role', 'option');
    termOption.setAttribute('aria-selected', 'false');

    const submit = document.createElement('button');
    submit.type = 'submit';
    submit.className = 'predictive-search__item predictive-search__item--term link link--text h5';
    submit.tabIndex = -1;
    const form = this.querySelector('form.search');
    if (form && form.id) submit.setAttribute('form', form.id);
    submit.textContent = products.length
      ? `عرض كل النتائج لـ «${searchTerm}»`
      : `عرض نتائج البحث عن «${searchTerm}»`;
    termOption.appendChild(submit);
    list.appendChild(termOption);

    results.appendChild(list);

    const status = document.createElement('span');
    status.hidden = true;
    status.setAttribute('data-predictive-search-live-region-count-value', '');
    status.textContent = `${products.length} نتائج`;

    this.predictiveSearchResults.innerHTML = '';
    this.predictiveSearchResults.append(results, status);
    this.setAttribute('results', true);
    this.setLiveRegionResults();
    this.open();
  }

  renderSearchFallback(searchTerm) {
    if (!searchTerm) {
      this.close();
      return;
    }

    this.predictiveSearchResults.innerHTML = '';

    const results = document.createElement('div');
    results.id = 'predictive-search-results';

    const list = document.createElement('ul');
    list.id = 'predictive-search-results-list';
    list.className = 'predictive-search__results-list list-unstyled';
    list.setAttribute('role', 'listbox');

    const option = document.createElement('li');
    option.id = 'predictive-search-option-search-keywords';
    option.className = 'predictive-search__list-item';
    option.setAttribute('role', 'option');
    option.setAttribute('aria-selected', 'false');

    const submit = document.createElement('button');
    submit.type = 'submit';
    submit.className = 'predictive-search__item predictive-search__item--term link link--text h5';
    submit.tabIndex = -1;
    const form = this.querySelector('form.search');
    if (form && form.id) submit.setAttribute('form', form.id);
    submit.textContent = `عرض نتائج البحث عن «${searchTerm}»`;

    option.appendChild(submit);
    list.appendChild(option);
    results.appendChild(list);

    const status = document.createElement('span');
    status.hidden = true;
    status.setAttribute('data-predictive-search-live-region-count-value', '');
    status.textContent = `يمكنك عرض نتائج البحث عن ${searchTerm}`;

    this.predictiveSearchResults.append(results, status);
    this.setAttribute('results', true);
    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      if (this.statusElement) this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  hasVisibleResults() {
    return Boolean(this.querySelector('.predictive-search__item--link, .predictive-search__item--term'));
  }

  bindTermButtonsToForm() {
    const form = this.querySelector('form.search');
    if (!form || !form.id) return;
    this.querySelectorAll('.predictive-search__item--term').forEach((button) => {
      button.setAttribute('form', form.id);
      if (!button.getAttribute('type')) button.setAttribute('type', 'submit');
    });
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    this.bindTermButtonsToForm();

    this.setLiveRegionResults();
    if (this.hasVisibleResults()) {
      this.open();
    } else {
      this.renderSearchFallback(this.getQuery());
    }
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const live = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (live) this.setLiveRegionText(live.textContent);
  }

  getResultsMaxHeight() {
    const headerSection = document.getElementById('shopify-section-header') || this.closest('.shopify-section');
    const headerBottom = headerSection ? headerSection.getBoundingClientRect().bottom : this.getBoundingClientRect().bottom;
    this.resultsMaxHeight = Math.max(180, window.innerHeight - headerBottom - 12);
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.searchTicket = (this.searchTicket || 0) + 1;
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }

    this.removeAttribute('loading');

    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false
    this.predictiveSearchResults.removeAttribute('style');

    this.isOpen = false;
  }
}

customElements.define('predictive-search', PredictiveSearch);
